// src/utils/auth.js

/**
 * Authentication utility functions for CHEF AI Dashboard
 * Handles both local development and production authentication flows
 * with proper environment awareness
 */

// Environment configuration
let config = {
  localEnabled: process.env.REACT_APP_LOCAL_ENABLED === "true",
  integrationEnabled: process.env.REACT_APP_INTEGRATION_ENABLED === "true",
  prodEnabled: process.env.REACT_APP_PROD_ENABLED === "true",
  environment: process.env.REACT_APP_ENVIRONMENT || "",
  homeUrl: process.env.REACT_APP_HOME_URL || "https://chefaicookware.com",
  apiBaseUrl: process.env.REACT_APP_API_BASE_URL,
};

// Runtime environment detection for GitHub Pages deployment
// This ensures correct environment settings even if build variables are wrong
if (window.location.hostname === "integration.dashboard.chefaicookware.com") {
  console.log("⚠️ Runtime environment detection - forcing integration settings");
  config = {
    ...config,
    localEnabled: false,
    integrationEnabled: true,
    environment: "integration",
    homeUrl: "https://chefaicookware.com",
    apiBaseUrl: "https://int.api.chefaicookware.com/integration",
  };
}

/**
 * Extracts a specific cookie value by name
 * @param {string} name - The name of the cookie to retrieve
 * @returns {string|null} The cookie value or null if not found
 */
export const getCookie = (name) => {
  const cookiePrefix = `${name}=`;
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookieArray = decodedCookie.split(";");

  for (let i = 0; i < cookieArray.length; i++) {
    let cookie = cookieArray[i].trim();
    if (cookie.indexOf(cookiePrefix) === 0) {
      return cookie.substring(cookiePrefix.length, cookie.length);
    }
  }
  return null;
};

/**
 * Sets a cookie with proper domain and security settings
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {number} days - Days until expiration
 */
const setCookie = (name, value, days = 7) => {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = `expires=${date.toUTCString()}`;

  // Set domain based on environment
  const domain = config.localEnabled ? "localhost" : ".chefaicookware.com";
  const secure = config.localEnabled ? "" : "secure;";

  document.cookie = `${name}=${value}; ${expires}; domain=${domain}; path=/; ${secure} samesite=strict`;
};

/**
 * Retrieves the authentication token from cookies
 * @returns {string|null} The auth token or null if not found
 */
export const getAuthFromCookies = () => {
  return getCookie("auth_token");
};

/**
 * Retrieves and parses the user data from cookies
 * @returns {Object|null} The parsed user object or null if not found
 */
export const getUserFromCookies = () => {
  const userCookie = getCookie("auth_user");

  if (userCookie) {
    try {
      return JSON.parse(userCookie);
    } catch (e) {
      console.error("Error parsing user data from cookie:", e);
      return null;
    }
  }
  return null;
};

/**
 * Initializes authentication by checking both localStorage and cookies
 * This function supports the cross-subdomain authentication flow
 * @returns {Object} An object containing token and user data
 */
export const initializeAuth = () => {
  // First check localStorage
  let token = localStorage.getItem("accessToken");
  let user = localStorage.getItem("user");

  // If not in localStorage, try cookies
  if (!token) {
    token = getAuthFromCookies();
    if (token) {
      localStorage.setItem("accessToken", token);
      console.log("Restored auth token from cookie to localStorage");
    }
  }

  if (!user) {
    const cookieUser = getUserFromCookies();
    if (cookieUser) {
      localStorage.setItem("user", JSON.stringify(cookieUser));
      user = JSON.stringify(cookieUser);
      console.log("Restored user data from cookie to localStorage");
    }
  }

  // Safely parse user data if it's a string, or use directly if it's already an object
  let parsedUser = null;
  if (user) {
    try {
      // Check if already parsed - this handles cases where user is stored directly in localStorage
      parsedUser = typeof user === "string" ? JSON.parse(user) : user;

      // Additional validation to ensure we have a valid user object
      if (!parsedUser || typeof parsedUser !== "object") {
        console.error("Invalid user data format");
        parsedUser = null;
      }
    } catch (e) {
      console.error("Error parsing user data:", e);
      parsedUser = null;
    }
  }

  return {
    token,
    user: parsedUser,
  };
};

/**
 * Saves authentication data to both localStorage and cookies
 * @param {string} token - Authentication token
 * @param {Object} user - User data object
 */
export const saveAuthData = (token, user) => {
  // Save to localStorage
  localStorage.setItem("accessToken", token);
  localStorage.setItem("user", JSON.stringify(user));

  // Save to cookies for cross-subdomain access
  setCookie("auth_token", token);
  setCookie("auth_user", JSON.stringify(user));
};

/**
 * Clears all authentication data from localStorage and cookies
 * Used for logout functionality
 */
export const clearAuth = () => {
  // Clear localStorage
  localStorage.removeItem("accessToken");
  localStorage.removeItem("user");

  // Clear cookies
  const domain = config.localEnabled ? "localhost" : ".chefaicookware.com";
  document.cookie = `auth_token=; domain=${domain}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; secure; samesite=strict`;
  document.cookie = `auth_user=; domain=${domain}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; secure; samesite=strict`;

  console.log("Authentication data cleared");
};

/**
 * Checks if the user is authenticated
 * @returns {boolean} True if token exists, false otherwise
 */
export const isAuthenticated = () => {
  const { token } = initializeAuth();
  return !!token;
};

/**
 * Gets the current user data
 * @returns {Object|null} The user object or null if not authenticated
 */
export const getCurrentUser = () => {
  const { user } = initializeAuth();
  return user;
};

/**
 * Gets the current authentication token
 * @returns {string|null} The auth token or null if not authenticated
 */
export const getAuthToken = () => {
  const { token } = initializeAuth();
  return token;
};

/**
 * Gets the appropriate login URL based on environment
 * @returns {string} The login URL
 */
export const getLoginUrl = () => {
  // Get current domain information
  const isCustomDomain = window.location.hostname.includes("chefaicookware.com");
  const currentHost = window.location.origin;

  // Log environment information for debugging
  console.log("Login URL context:", {
    localEnabled: config.localEnabled,
    integrationEnabled: config.integrationEnabled,
    environment: config.environment,
    isCustomDomain,
    homeUrl: config.homeUrl,
    currentUrl: window.location.href,
  });

  // Return the appropriate login URL based on environment
  if (config.localEnabled) {
    // Local development - use the configured home URL
    return `${config.homeUrl}/login`;
  } else if (isCustomDomain) {
    // Custom domain - append return_to parameter for dashboard
    const returnUrl = encodeURIComponent(`${currentHost}/dashboard`);
    return `${config.homeUrl}/login?return_to=${returnUrl}`;
  } else {
    // GitHub Pages - simplest approach
    return `${config.homeUrl}/login`;
  }
};

// For debugging - log the configuration
console.log("Auth config:", {
  localEnabled: config.localEnabled,
  integrationEnabled: config.integrationEnabled,
  environment: config.environment,
  homeUrl: config.homeUrl,
  loginUrl: getLoginUrl(),
  currentUrl: window.location.href,
});

/**
 * Gets the appropriate API base URL based on environment
 * @returns {string} The API base URL
 */
export const getApiBaseUrl = () => {
  return config.apiBaseUrl;
};

/**
 * Gets the current environment configuration
 * @returns {Object} Environment configuration
 */
export const getEnvironmentConfig = () => {
  return config;
};
